import React from 'react';
import { Flex } from '@upsales/components';
import ListViewFilters from 'App/components/ListView/ListViewFilters';
import ListViewQuickSearch from 'App/components/ListView/ListViewQuickSearch';
import { RenderHeaderProvided } from 'App/components/ListView/ListViewRenderHelpers';
import './SalesCardFilterHeader.scss';

type Props<ItemType extends {}> = {
	provided: RenderHeaderProvided<ItemType>;
	/** The list's title element, rendered to the left of the filter controls. */
	title: React.ReactNode;
	/** Optional extra controls (e.g. an add button) rendered alongside the filters. */
	extra?: React.ReactNode;
};

/**
 * Header for the company-card sales lists that surfaces the free-text search box
 * and the `ListViewFilters` menu next to the list title. The card lists supply
 * their own `renderHeader`, which bypasses `renderDefaultHeader` where these
 * controls normally live — so we render them here instead.
 *
 * Renders the filter menu only when `filterConfigs` is present, making it a no-op
 * when the feature flag is off and no curated filters are passed.
 */
const SalesCardFilterHeader = <ItemType extends {}>({ provided, title, extra }: Props<ItemType>) => {
	const { filterConfigs, filters, onFilterChange, hasChanged, customFields, setFiltersVisible, onChangeView, quickSearchProps } =
		provided;

	// Use the card's own header class (padding only). Do NOT spread `provided.props`
	// — its className is ListView's full-page `ListView__header` (sticky, fixed
	// height, z-index 12), which is wrong for an embedded card panel.
	return (
		<Flex className="SimpleListView__header SalesCardFilterHeader" alignItems="center" justifyContent="space-between">
			{title}
			<Flex alignItems="center" gap="u2">
				{extra}
				{quickSearchProps ? <ListViewQuickSearch {...quickSearchProps} /> : null}
				{filterConfigs ? (
					<ListViewFilters
						onVisibleChange={setFiltersVisible}
						filterConfigs={filterConfigs}
						activeFilters={filters}
						onChange={onFilterChange}
						hiddenFilters={['Labels', 'ListSearch']}
						hasChanged={hasChanged}
						customFields={customFields}
						onViewChange={onChangeView}
						listType="order"
						ignoreEntityType
						opts={{ noTabs: true }}
					/>
				) : null}
			</Flex>
		</Flex>
	);
};

export default SalesCardFilterHeader;
